// The seans drawer (day-view.md §7; v3, Onur 13.10.2026): what a tap
// on a cell opens. Three parts, top to bottom. The time: who, when and
// with whom, where the seans stands (its first chip), and the chips
// that act on the calendar — Geliyor, Saati değiştir, WhatsApp. The
// money: this seans' fee, where the account stands, and the round
// buttons — Ödeme al, which opens the pay sheet (Onur 14.10.2026: a
// mis-tap must cost nothing), and İptal with its reasons in place. A
// glance back: the last seanslar and the newest note. What the old
// drawer wrote out in full (the calendar and the program, the WhatsApp
// row, the money position, every note) opens over it in a sheet of its
// own and comes back here.
// On a wide screen it is the panel on the right, on a tablet the
// bottom sheet. Nothing here needs hover.

import {useState} from "react";
import type {ReactNode} from "react";
import type {
  BoardCancelled, BoardCell, BoardClient, BoardRecorded, CancellationView, CancelledBy, CellFlag,
  ClientMoneyView, ClientNoteView, ScheduleView, SessionClientDetail, SessionDetail,
  SessionEventView, SessionHistoryRow, SozlesmeMark, WaRowView,
} from "../api/contract";
import {
  AUTO_CANCEL_TR, CANCELLED_BY_OPTIONS, CANCELLED_BY_TR, CANCEL_REASONS, CATEGORY_TR,
  CHANNEL_TR, DELIVERY_TR, FLAG_TR, FORMAT_TR, RECURRENCE_TR, ROOM_TR, SERVICE_KIND_TR,
  SOURCE_TR, STATUS_TR, WA_REPLY_TR, WEEKDAY_TR, acctMarks, birthdayWords, cancelledByOf, capTr,
  dueToday, flipTarget, hasStarted, historyView, payPlan, seansState, trDay, trDayName, trMonth,
  trStamp, waChipWords, waWord, whenLine, type CancelReason, type SeansStateKind,
} from "../labels";
import {useMoney} from "../ui/blind-context";
import {Avatar} from "../ui/Avatar";
import {CakeMark} from "../ui/Cake";
import {Icon, type IconName} from "../ui/Icon";
import {Sheet} from "./Sheets";
import {chatLink, type ClientRef} from "./useActions";

// What the drawer is about: a live cell, or a cancelled slot.
export type Anchor = {cell: BoardCell; rec?: undefined} | {rec: BoardCancelled; cell?: undefined};

export interface DrawerActions {
  busy: string | null;
  onPay: (cell: BoardCell, client: BoardClient) => void; // the pay sheet — "Ödeme al"
  // A recorded seans: back to not done, not paid — or its tahsilat to
  // the other channel. Both open a one-line confirm.
  onUndo: (cell: BoardCell, client: BoardClient) => void;
  onChannel: (cell: BoardCell, client: BoardClient, to: "cash" | "bank") => void;
  onCancelNow: (cell: BoardCell, reason: string, who: CancelledBy) => void; // İptal, with its reason
  onNote: (client: ClientRef) => void;
  onClient: (client: ClientRef) => void;
  onWhatsapp: (cell: BoardCell, client: BoardClient) => void; // send the confirmation
  // "Geliyor": the family said on the phone they are coming; undo
  // takes a phone confirmation back.
  onPhoneConfirm: (cell: BoardCell, client: BoardClient, undo: boolean) => void;
  onRestore: (cancellationId: string) => void;
  onMove: (cell: BoardCell) => void; // "Saati değiştir" — the drag without a mouse
  // The sheets that open over the drawer and come back to it.
  onWaSheet: () => void;
  onSeansInfo: () => void;
  onHistory: (client: ClientRef) => void;
  onNotes: (client: ClientRef) => void;
}

export interface DrawerProps {
  anchor: Anchor;
  providerName: string;
  providerPhoto?: boolean; // the board says she has a portrait
  money: boolean;
  detail: SessionDetail | null;
  loading: boolean;
  error: string | null;
  actions: DrawerActions;
  onClose: () => void;
  today: string; // the desk's day — "Bugün", and what has started
  nowMin: number; // the Istanbul minute on `today`
}

// The status chip's look: green when it went well, amber when the
// desk has a record to make, red when the family is not coming, grey
// otherwise. The grid's sign for planlı is the open circle.
const STATE_LOOK: Record<SeansStateKind, {tone: string; icon: IconName}> = {
  done: {tone: "on", icon: "check"}, confirmed: {tone: "on", icon: "check"},
  partial: {tone: "warn", icon: "alert"}, unrecorded: {tone: "warn", icon: "alert"},
  declined: {tone: "bad", icon: "x"}, cancelled: {tone: "off", icon: "x"},
  paused: {tone: "off", icon: "pause"}, planned: {tone: "off", icon: "circle"},
};

// The board's flags on the cell, each with a line icon (FLAG_TR's
// glyphs are the grid's).
const FLAG_ICON: Record<CellFlag, IconName> = {
  unrecorded_past: "alert", still_on_calendar: "alert", partially_recorded: "check",
  unresolved_client: "user",
};

// ── The drawer ────────────────────────────────────────────

// A child of the seans as the drawer needs it: the board's view when
// the cell is live, the detail's card when it has come.
interface Kid {
  ref: ClientRef;
  first: string; // the first name — a group's chips say whose they are
  board: BoardClient | null;
  detail: SessionClientDetail | undefined;
  noCard: boolean; // outside the caller's caseload: a name, no card
  sozlesme: {mark: SozlesmeMark; text: string | null} | null;
  sozlesmeLink: string | null; // the current form's PDF, once the detail came
}

function kidOf(c: {clientId: string; name: string}, board: BoardClient | null,
  detail: SessionClientDetail | undefined): Kid {
  const basics = detail?.basics ?? null;
  return {
    ref: {clientId: c.clientId, name: c.name,
      deskNote: board?.deskNote ?? basics?.deskNote?.text ?? null},
    first: c.name.split(" ")[0],
    board, detail,
    noCard: detail !== undefined && basics === null,
    sozlesme: board?.sozlesme ?? basics?.sozlesme ?? null,
    sozlesmeLink: basics?.sozlesmeLink ?? null,
  };
}

const anchorName = (a: Anchor): string => a.cell ?
  a.cell.clients.map((c) => c.name).join(", ") || a.cell.title :
  a.rec.clientNames.join(", ") || "—";

export function SessionDrawer(p: DrawerProps) {
  const {cell, rec} = p.anchor;
  const {actions} = p;
  const locked = actions.busy !== null;
  const date = cell ? cell.start.slice(0, 10) : p.detail?.date ?? "";
  const time = cell ? cell.time : rec.time;
  const names = anchorName(p.anchor);
  const cancelled = !!rec || cell.status === "cancelled" || cell.status === "paused";
  const paused = rec ? rec.kind === "paused" : cell.status === "paused";
  const chat = chatLink(`Nefes: ${date} ${time ?? "—"} ${names} — ${p.providerName} seansı: `);
  // "Geri al": the slot's record, or the standing record a cell still
  // carries while its removal has not completed.
  const restoreId = rec ? rec.cancellationId : cell.cancellation?.cancellationId ?? null;
  const restorable = rec ? rec.restorable : p.detail?.cancellation?.restorable ?? true;
  const restoring = restoreId !== null && actions.busy === `uncancel:${restoreId}`;
  const why = rec ? {who: rec.cancelledBy, reason: rec.reason} :
    p.detail?.cancellation?.standing ?
      {who: p.detail.cancellation.cancelledBy, reason: p.detail.cancellation.reason} : null;
  // The door refuses the phone confirmation and the WhatsApp send once
  // the seans has begun, so the drawer stops offering them.
  const started = !cell || hasStarted(date, cell.time, p.today, p.nowMin);
  const [asking, setAsking] = useState(false); // a group's İptal, its reasons open

  const detailFor = (clientId: string): SessionClientDetail | undefined =>
    p.detail?.clients.find((c) => c.clientId === clientId);
  // A cancelled slot has no board clients: the detail's, else the
  // record's names while it loads.
  const kids: Kid[] = cell ?
    cell.clients.map((c) => kidOf(c, c, detailFor(c.clientId))) :
    (p.detail?.clients ?? rec.clientIds.map((clientId, i) =>
      ({clientId, name: rec.clientNames[i] ?? clientId})))
      .map((c) => kidOf(c, null, detailFor(c.clientId)));
  const group = kids.length > 1;
  const one = kids.length === 1 ? kids[0] : null;
  const board = cell?.clients ?? [];
  const recordedAny = board.some((c) => c.recorded);
  const recordedAll = board.length > 0 && board.every((c) => c.recorded);
  // A recorded seans is neither cancelled nor moved (Onur, 13.10.2026):
  // "Geri al" first. İptal is the seans' — in the one child's money
  // card, else (a group, a cell with no child) a chip up here.
  const canCancel = !!cell && !cancelled && !recordedAny;
  const cancelChip = canCancel && board.length !== 1;
  // Where the seans stands — the head's first chip; a slot's is its
  // record's. Before the start the Geliyor chips say a confirmation
  // themselves (and take a phone one back), so it does not say it twice.
  const solo = !group ? board[0] : undefined;
  const state = seansState(rec ? (paused ? "paused" : "cancelled") : cell.status,
    cell?.flags ?? [], solo?.attendance.state === "confirmed" ?
      solo.attendance.source === "phone" ? "phone" : "whatsapp" : null);
  const stateShown = !(cell && !cancelled && !started && state.kind === "confirmed");
  // The WhatsApp chip opens what was asked and what came back — the
  // answer, a written reply, who took the phone call, a failed send —
  // so it stays after the start, on an iptal and on a slot; only the
  // send in its sheet stops. Where nothing was asked it is there only
  // while a send is still possible. A phone confirmation is left to
  // the Geliyor chip before the start, to the status chip after it.
  const coming = (c: BoardClient) => !!cell && !cancelled && !c.recorded &&
    (!started || state.kind === "confirmed");
  const waRows = cell ?
    board.map((c) => ({who: group ? c.name.split(" ")[0] : null, state: c.attendance.state,
      source: c.attendance.source, coming: coming(c)})) :
    kids.flatMap((k) => k.detail?.whatsapp ? [{who: group ? k.first : null,
      state: k.detail.whatsapp.state, source: k.detail.whatsapp.response?.source,
      coming: false}] : []);
  const waAsked = waRows.some((r) => r.state !== "none");
  const waShown = waAsked || (!!cell && board.length > 0 && !started && !recordedAll && !cancelled);
  const waWords = waChipWords(waRows);

  const when = !date ? "" : time ?
    whenLine(p.detail?.schedule ?? null, date, time, p.today) :
    date === p.today ? "Bugün" : trDayName(date);

  const head = (
    <header className="sd-head">
      <div className="sd-who">
        {one && !one.noCard ? (
          <button className="sd-name" disabled={locked} title="Danışan bilgisi"
            onClick={() => actions.onClient(one.ref)}>
            <b>{one.ref.name}</b><Icon name="chev" />
          </button>
        ) : group ? (
          // Each child's name is the way to their Danışan bilgisi (and
          // its phone numbers) — on an iptal and a slot too, where no
          // money card carries it.
          <h2 className="sd-name sd-names">
            {kids.map((k) => k.noCard ? <b key={k.ref.clientId}>{k.ref.name}</b> : (
              <button key={k.ref.clientId} disabled={locked} title="Danışan bilgisi"
                onClick={() => actions.onClient(k.ref)}>
                <b>{k.ref.name}</b><Icon name="chev" />
              </button>
            ))}
          </h2>
        ) : <h2 className="sd-name"><b>{names}</b></h2>}
        <Spin on={p.loading} />
        <button className="sd-round" aria-label="Kapat" onClick={p.onClose}>
          <Icon name="x" />
        </button>
      </div>
      <button className="sd-when" disabled={locked} title="Seans bilgisi"
        onClick={actions.onSeansInfo}>
        {when && <span>{when} ·</span>}
        <Avatar providerId={p.anchor.cell ? p.anchor.cell.providerId : p.anchor.rec.providerId}
          name={p.providerName} photo={p.providerPhoto} />
        <span>{p.providerName}</span><Icon name="chev" />
      </button>
      <div className="sd-chips">
        {stateShown && (
          <span className={`sd-chip state ${STATE_LOOK[state.kind].tone}`}>
            <Icon name={STATE_LOOK[state.kind].icon} />{state.word}
          </span>
        )}
        {cell && !cancelled && !started && board.filter((c) => !c.recorded).map((c) => (
          <ComingChip key={c.clientId} cell={cell} client={c} named={group} busy={actions.busy}
            onConfirm={actions.onPhoneConfirm} />
        ))}
        {cell && !cancelled && !recordedAny && (
          <button className="sd-chip" disabled={locked} onClick={() => actions.onMove(cell)}>
            <Icon name="clock" />Saati değiştir
          </button>
        )}
        {cancelChip && (
          <button className={asking ? "sd-chip pressed" : "sd-chip"} aria-expanded={asking}
            disabled={locked} onClick={() => setAsking(!asking)}>
            <Icon name="x" />İptal
          </button>
        )}
        {waShown && (
          <button className="sd-chip" disabled={locked} onClick={actions.onWaSheet}>
            <Icon name="msg" />
            <span className="sd-ell">
              WhatsApp
              {waWords.map((w) => (
                <span key={`${w.who}-${w.word}`} className={w.tone ? `soft ${w.tone}` : "soft"}>
                  {" · "}{w.who && `${w.who} `}{w.word}
                </span>
              ))}
            </span>
          </button>
        )}
        {kids.map((k) => <InfoChips key={k.ref.clientId} kid={k} named={group} today={p.today} />)}
        {/* "Kayıt yok" and "kısmen kayıtlı" are the status chip's words. */}
        {cell?.flags.filter((f) => f !== "unrecorded_past" && f !== "partially_recorded")
          .map((f) => (
            <span key={f} className="sd-chip warn">
              <Icon name={FLAG_ICON[f]} />{FLAG_TR[f].word}
            </span>
          ))}
      </div>
    </header>
  );

  return (
    <Sheet className="seans-sheet sd-drawer" head={head} onClose={p.onClose}>
      {p.error && <p className="banner error">{p.error}</p>}
      {p.detail?.warnings.map((w) => <p key={w} className="banner warn">{w}</p>)}

      {cell && cell.clients.length === 0 && (
        <section className="sd-card sd-pad">
          <p className="sd-quiet">Bu etkinlik bir danışana bağlı değil — eylemler sohbette.</p>
        </section>
      )}
      {cell && cancelChip && asking && (
        <section className="sd-card sd-money">
          <CancelAsk alone cell={cell} busy={actions.busy}
            onPick={(reason, who) => actions.onCancelNow(cell, reason, who)}
            onBack={() => setAsking(false)} />
        </section>
      )}
      {cancelled && (
        <CancelledCard paused={paused} why={why} restoreId={restoreId} restorable={restorable}
          restoring={restoring} locked={locked} onRestore={actions.onRestore} />
      )}
      {cell && !cancelled && cell.clients.map((c) => (
        <MoneyCard key={c.clientId} cell={cell} client={c} detail={detailFor(c.clientId)}
          group={group} canCancel={canCancel && !group} money={p.money} actions={actions} />
      ))}

      {/* The glance waits for the read: Son seanslar comes with it, and
          a card landing over Notlar would push it down (Onur 15.10.2026:
          nothing on the drawer moves while it loads). */}
      {(p.detail || !p.loading) && kids.map((k) => (
        <Glance key={k.ref.clientId} kid={k} named={group} money={p.money}
          providerId={cell ? cell.providerId : rec.providerId} today={p.today} actions={actions} />
      ))}

      <a className="sd-foot" href={chat} target="_blank" rel="noreferrer">Sohbette aç ↗</a>
    </Sheet>
  );
}

// ── The time: chips ───────────────────────────────────────

// "Geliyor": the other answer a confirmation call gets. One per child
// — a group seans is confirmed child by child, so a group's chips say
// whose. Pressed again it takes the phone confirmation back (a
// mis-tap); a guardian's own WhatsApp answer stays, and is not a
// button.
function ComingChip({cell, client, named, busy, onConfirm}: {
  cell: BoardCell; client: BoardClient; named: boolean; busy: string | null;
  onConfirm: DrawerActions["onPhoneConfirm"];
}) {
  const {state, source} = client.attendance;
  const mine = state === "confirmed" && source === "phone";
  const byWa = state === "confirmed" && !mine;
  const running = busy === `phone:${cell.id}:${client.clientId}`;
  const word = named ? `${client.name.split(" ")[0]} geliyor` : "Geliyor";
  if (byWa) {
    return (
      <span className="sd-chip on" title="Veli WhatsApp'tan onayladı">
        <Icon name="check" />{word} · WhatsApp
      </span>
    );
  }
  return (
    <button className={mine ? "sd-chip on" : "sd-chip"} aria-pressed={mine}
      disabled={busy !== null}
      title={mine ? "Telefon onayını geri al" : "Veli telefonda geleceğini söyledi"}
      onClick={() => onConfirm(cell, client, mine)}>
      <Icon name="check" />{running ? "Kaydediliyor…" : mine ? `${word} · telefon` : word}
    </button>
  );
}

// What the desk must see before the family reaches it: a birthday near
// the seans (Onur, 23.10.2026), the sözleşme that is not signed, the
// pinned desk note. The form itself opens in a new tab from
// "Sözleşme ↗", signed or not (Onur, 20.10.2026).
function InfoChips({kid, named, today}: {kid: Kid; named: boolean; today: string}) {
  const whose = named ? `${kid.first}: ` : "";
  const note = kid.ref.deskNote;
  const bday = kid.board?.birthday;
  return (
    <>
      {bday && (
        <span className="sd-chip bday">
          <CakeMark />{whose}{birthdayWords(bday, today)}
        </span>
      )}
      {kid.sozlesme && kid.sozlesme.mark !== "ok" && (
        <span className="sd-chip warn">
          <Icon name="note" />{whose}{kid.sozlesme.text ?? "sözleşme"}
        </span>
      )}
      {kid.sozlesmeLink && (
        <a className="sd-chip" href={kid.sozlesmeLink} target="_blank" rel="noreferrer">
          {whose}Sözleşme ↗
        </a>
      )}
      {note && (
        <span className="sd-chip warn" title={note}>
          <Icon name="pin" /><span className="sd-ell">{whose}{note}</span>
        </span>
      )}
    </>
  );
}

// While the read is on its way: a ring left of ×, in place of the
// "Ayrıntılar yükleniyor…" line that stood over the cards and let them
// jump up when it went (Onur 15.10.2026). It keeps its room when idle,
// so the name beside it never rewraps.
function Spin({on}: {on: boolean}) {
  return (
    <span className={on ? "sd-spin on" : "sd-spin"} role="status">
      {on && <span className="sr-only">Ayrıntılar yükleniyor</span>}
    </span>
  );
}

// ── The money ─────────────────────────────────────────────

// One child's card: the fee with the round buttons on its right, then
// the account. İptal's reasons open under them, in the buttons' place.
function MoneyCard({cell, client, detail, group, canCancel, money, actions}: {
  cell: BoardCell; client: BoardClient; detail: SessionClientDetail | undefined;
  group: boolean; canCancel: boolean; money: boolean; actions: DrawerActions;
}) {
  const {tl, num, text} = useMoney();
  const [asking, setAsking] = useState(false);
  const rec = client.recorded;
  const locked = actions.busy !== null;
  const fee = client.money?.feeTL ?? null;
  // What the pay sheet will take, under Ödeme al: its Bugün alınacak,
  // else that credit or a fee of 0 pays the seans. Nothing for a
  // login without figures or a pair without a fee.
  const plan = money && !rec && client.money ? payPlan(client.money) : null;
  const marks = money ? acctMarks(client.money, !rec) : [];
  const noCard = detail !== undefined && detail.basics === null;
  const ref: ClientRef = {clientId: client.clientId, name: client.name,
    deskNote: client.deskNote ?? detail?.basics?.deskNote?.text ?? null};
  // Nakit ⇄ havale only for one seans whose own tahsilat (not credit
  // it consumed) went through exactly one of the two — a havale booked
  // iban_owner (Seda's IBAN) or, before 11.10.2026, bank.
  const ch = rec?.serviceIds.length === 1 && rec.linkedChannels?.length === 1 ?
    rec.linkedChannels[0] : null;
  const flipTo = flipTarget(ch);
  const paySub = !plan ? undefined : plan.kind === "take" ? tl(plan.dueTL) :
    plan.kind === "credit" ? "krediden düşer" : "ücretsiz";

  return (
    <section className="sd-card sd-money" aria-label={group ? client.name : "Bu seans"}>
      {group ? (
        <button className="sd-label sd-kid" disabled={noCard || locked}
          title="Danışan bilgisi" onClick={() => actions.onClient(ref)}>
          <b>{client.name}</b>{!noCard && <Icon name="chev" />}
        </button>
      ) : <span className="sd-label">Bu seans</span>}
      {money && (fee === null ?
        <div className="sd-figure none">Ücret tanımsız</div> :
        <div className="sd-figure">{num(fee)} <span className="sd-cur">TL</span></div>)}
      {!asking && (
        <div className="sd-acts">
          {rec ? (
            <>
              <Act icon="undo" label="Geri al" disabled={locked}
                title="Seans yapılmadı, ödeme alınmadı olarak geri döner"
                onClick={() => actions.onUndo(cell, client)} />
              {flipTo && (
                <Act icon="swap" label={flipTo === "cash" ? "Nakit'e çevir" : "Havale'ye çevir"}
                  disabled={locked} onClick={() => actions.onChannel(cell, client, flipTo)} />
              )}
            </>
          ) : (
            <>
              {/* The pay sheet's own button records it: a mis-tap here
                  opens a sheet, it writes nothing. */}
              <Act primary icon="wallet" label="Ödeme al" sub={paySub} disabled={locked}
                onClick={() => actions.onPay(cell, client)} />
              {canCancel && (
                <Act icon="x" label="İptal" disabled={locked} onClick={() => setAsking(true)} />
              )}
            </>
          )}
        </div>
      )}
      {(rec || marks.length > 0) && (
        <div className="sd-acct">
          {rec && <DoneChip recorded={rec} money={money} />}
          {/* Where the account stands, not a button: Ödeme al takes the
              old debt too, Son seanslar → Tümü shows where it came from. */}
          {marks.map((a) => (
            <span key={a.kind} className={`sd-chip ${a.kind}`}>
              <span className="sd-dot" />{tl(a.amountTL)} {a.word}
            </span>
          ))}
        </div>
      )}
      {money && detail?.money?.warnings.map((w) => <p key={w} className="sd-warn">{text(w)}</p>)}
      {asking && (
        <CancelAsk cell={cell} busy={actions.busy}
          onPick={(reason, who) => actions.onCancelNow(cell, reason, who)}
          onBack={() => setAsking(false)} />
      )}
    </section>
  );
}

function Act({icon, label, sub, primary, running, title, disabled, onClick}: {
  icon: IconName; label: string; sub?: string; primary?: boolean;
  running?: boolean; // this one's write is under way: it stays lit
  title?: string; disabled: boolean; onClick: () => void;
}) {
  return (
    <button className={primary ? "sd-act primary" : "sd-act"} title={title} disabled={disabled}
      aria-busy={running || undefined} onClick={onClick}>
      <span className="sd-circle"><Icon name={icon} /></span>
      <b>{label}</b>
      {sub && <small>{sub}</small>}
    </button>
  );
}

// A recorded seans, read off the board: how it was paid.
function DoneChip({recorded, money}: {recorded: BoardRecorded; money: boolean}) {
  const {tl} = useMoney();
  if (!money || recorded.paidTL === undefined) {
    return (
      <span className="sd-chip done">
        <Icon name="check" />{recorded.covered ? "Ödendi" : "Kaydedildi"}
      </span>
    );
  }
  const paid = recorded.paidTL;
  const left = recorded.uncoveredTL ?? 0;
  // One word per kind: a bank and an iban_owner payment are both havale.
  const words = [...new Set((recorded.linkedChannels ?? []).map((c) => CHANNEL_TR[c] ?? c))];
  if (paid === 0) {
    return recorded.covered ?
      <span className="sd-chip done"><Icon name="check" />Ücretsiz</span> :
      <span className="sd-chip debt"><span className="sd-dot" />Ödenmedi</span>;
  }
  // paidTL is all that covered the seans, the credit it used included,
  // so a tahsilat is named by its channel, not by that figure: "4.000
  // TL nakit alındı" would be cash that was partly credit (the toast
  // said what was taken).
  return (
    <>
      <span className="sd-chip done">
        <Icon name="check" />
        {words.length ? `${left > 0 ? "Kısmen ödendi" : "Ödendi"} · ${words.join(" + ")}` :
          `${tl(paid)} krediden`}
      </span>
      {left > 0 && <span className="sd-chip debt">kalan {tl(left)}</span>}
    </>
  );
}

// İptal in two taps: the reasons come where the buttons were, and a
// reason is the cancel. "Diğer…" opens a text box under them, with an
// İptal et of its own (Onur 15.10.2026: the old iptal sheet asked for
// the reason again).
const REASON_ICON: Record<CancelReason, IconName> = {
  "hasta": "thermo", "şehir dışı": "plane", "haber vermeden gelmedi": "userX",
  "danışman izinli": "calX", "hava koşulları": "rain",
};

function CancelAsk({cell, busy, alone, onPick, onBack}: {
  cell: BoardCell; busy: string | null; alone?: boolean;
  onPick: (reason: string, who: CancelledBy) => void; onBack: () => void;
}) {
  // null until the desk picks: the reason then says who (labels'
  // cancelledByOf) — and the reason under the pointer or the focus
  // lights its party, so "danışman izinli" shows Danışman before the tap.
  const [who, setWho] = useState<CancelledBy | null>(null);
  const [near, setNear] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [other, setOther] = useState<string | null>(null); // Diğer's text, null while shut
  const locked = busy !== null;
  const running = busy === `cancel:${cell.id}`;
  const shown = who ?? cancelledByOf(near ?? "");
  const typed = other?.trim() ?? "";
  const pick = (reason: string) => {
    setPicked(reason);
    onPick(reason, who ?? cancelledByOf(reason));
  };
  return (
    <div className={alone ? "sd-why alone" : "sd-why"}>
      <div className="sd-why-top">
        <b>{alone && cell.clients.length > 1 ? "Grup seansı — neden iptal?" : "Neden iptal?"}</b>
        <button className="sd-chip small" disabled={locked} onClick={onBack}>
          <Icon name="x" />Vazgeç
        </button>
      </div>
      <div className="sd-chips" role="group" aria-label="Sebep">
        {CANCEL_REASONS.map((r) => (
          <button key={r} className="sd-chip small" disabled={locked}
            onPointerEnter={() => setNear(r)} onPointerLeave={() => setNear(null)}
            onFocus={() => setNear(r)} onBlur={() => setNear(null)}
            onClick={() => pick(r)}>
            <Icon name={REASON_ICON[r]} />{running && picked === r ? "İptal ediliyor…" : capTr(r)}
          </button>
        ))}
        <button className={other === null ? "sd-chip small" : "sd-chip small pressed"}
          aria-expanded={other !== null} disabled={locked}
          onClick={() => setOther(other === null ? "" : null)}>
          <Icon name="note" />Diğer…
        </button>
      </div>
      {other !== null && (
        <form className="sd-other" onSubmit={(e) => {
          e.preventDefault();
          if (typed && !locked) pick(typed);
        }}>
          <input autoFocus placeholder="Sebebi yazın" aria-label="Sebep" maxLength={200}
            value={other} disabled={locked} onChange={(e) => setOther(e.target.value)}
            onKeyDown={(e) => {
              // Shuts the box; a second Escape closes the drawer.
              if (e.key === "Escape") {
                e.stopPropagation();
                setOther(null);
              }
            }} />
          <button type="submit" className="sd-chip small stop" disabled={locked || !typed}>
            {running && picked === typed ? "İptal ediliyor…" : "İptal et"}
          </button>
        </form>
      )}
      <div className="sd-who-cancels">
        İptal eden
        <span className="sd-seg" role="group" aria-label="İptal eden">
          {CANCELLED_BY_OPTIONS.map((o) => (
            <button key={o.who} className={shown === o.who ? "on" : ""} aria-pressed={shown === o.who}
              disabled={locked} onClick={() => setWho(o.who)}>
              {capTr(o.word)}
            </button>
          ))}
        </span>
      </div>
    </div>
  );
}

// A cancelled cell or slot: who and why, and the way back onto the
// calendar — greyed with its reason when the record has no time.
function CancelledCard({paused, why, restoreId, restorable, restoring, locked, onRestore}: {
  paused: boolean; why: {who: string; reason: string} | null; restoreId: string | null;
  restorable: boolean; restoring: boolean; locked: boolean;
  onRestore: (cancellationId: string) => void;
}) {
  return (
    <section className="sd-card sd-money">
      <span className="sd-label">Bu seans</span>
      <div className="sd-status">{paused ? "Ara verildi" : "İptal edildi"}</div>
      {why && <p className="sd-sub">{capTr(CANCELLED_BY_TR[why.who] ?? why.who)} · {why.reason}</p>}
      {restoreId && (
        <div className="sd-acts">
          <Act icon="undo" label={restoring ? "Geri alınıyor…" : "Geri al"} running={restoring}
            sub={restorable ? "takvime geri koy" : "saat bilgisi yok"}
            disabled={!restorable || locked}
            title={restorable ? undefined : "Kayıtta saat bilgisi yok — sohbette yeniden oluşturun"}
            onClick={() => onRestore(restoreId)} />
        </div>
      )}
    </section>
  );
}

// ── The glance back ───────────────────────────────────────

// Not düş is the board's, not the read's: when the read failed, the
// note can still be dropped.
function Glance({kid, named, money, providerId, today, actions}: {
  kid: Kid; named: boolean; money: boolean; providerId: string; today: string;
  actions: DrawerActions;
}) {
  const cd = kid.detail;
  const locked = actions.busy !== null;
  const of = named ? ` · ${kid.first}` : "";
  return (
    <>
      {money && cd?.history && (
        <section className="sd-card sd-list">
          <div className="sd-list-head">
            <h3>Son seanslar{of}</h3>
            <button className="sd-chip small" disabled={locked}
              onClick={() => actions.onHistory(kid.ref)}>
              Tümü<Icon name="chev" />
            </button>
          </div>
          {cd.history.length === 0 ? <p className="sd-quiet">Kayıtlı seans yok.</p> :
            <HistoryRows rows={cd.history.slice(0, 5)} providerId={providerId} today={today} />}
        </section>
      )}
      {/* A door that predates the history still sends the account:
          it stays one tap away. */}
      {money && cd && !cd.history && cd.money && (
        <section className="sd-card sd-list">
          <div className="sd-list-head">
            <h3>Hesap{of}</h3>
            <button className="sd-chip small" disabled={locked}
              onClick={() => actions.onHistory(kid.ref)}>
              Tümü<Icon name="chev" />
            </button>
          </div>
          <AccountLine m={cd.money} />
        </section>
      )}
      {kid.noCard ? (
        <section className="sd-card sd-pad">
          <p className="sd-quiet">{named && <b>{kid.ref.name}: </b>}Sizin danışanınız değil — kartı görünmez.</p>
        </section>
      ) : (
        <section className="sd-card sd-list">
          <div className="sd-list-head">
            <h3>Notlar{of}</h3>
            <button className="sd-chip small" disabled={locked} onClick={() => actions.onNote(kid.ref)}>
              <Icon name="plus" />Not düş
            </button>
          </div>
          {cd ? <NoteGlance notes={cd.notes} locked={locked} onAll={() => actions.onNotes(kid.ref)} /> :
            <p className="sd-quiet">Notlar okunamadı.</p>}
        </section>
      )}
    </>
  );
}

function AccountLine({m}: {m: ClientMoneyView}) {
  const {text} = useMoney();
  return <p className="sd-quiet">{text(m.explanation)}</p>;
}

function HistoryRows({rows, providerId, today}: {
  rows: SessionHistoryRow[]; providerId: string; today: string;
}) {
  const {tl} = useMoney();
  return (
    <ul className="sd-rows">
      {rows.map((r, i) => {
        const v = historyView(r, providerId, today);
        const m = v.money;
        return (
          <li key={r.serviceId ?? r.cancellationId ?? `${r.date}-${i}`}
            className={v.today && !v.cancelled ? "new" : undefined}>
            <span className={v.cancelled ? "sd-ic x" : "sd-ic"}>
              <Icon name={v.cancelled ? "x" : "check"} />
            </span>
            <div className="sd-t">
              <b>{v.day}{v.today && " · bugün"}</b>
              <small>{v.sub}</small>
            </div>
            <div className="sd-r">
              {v.feeTL !== null && v.feeTL > 0 && <b>{tl(v.feeTL)}</b>}
              {m.kind === "paid" && <small>ödendi</small>}
              {m.kind === "owed" && <small className="owe">borç</small>}
              {m.kind === "left" && <small className="owe">kalan {tl(m.leftTL)}</small>}
              {m.kind === "free" && <small>ücretsiz</small>}
              {m.kind === "none" && <small>ücret yok</small>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function NoteGlance({notes, locked, onAll}: {
  notes: ClientNoteView[] | null; locked: boolean; onAll: () => void;
}) {
  if (notes === null) return <p className="sd-quiet">Not okuma yetkiniz yok.</p>;
  const [first] = notes;
  if (!first) return <p className="sd-quiet">Not yok.</p>;
  return (
    <>
      <NoteItem n={first} clamp />
      {notes.length > 1 && (
        <button className="sd-more" disabled={locked} onClick={onAll}>
          {notes.length - 1} not daha
        </button>
      )}
    </>
  );
}

function NoteItem({n, clamp}: {n: ClientNoteView; clamp?: boolean}) {
  return (
    <div className="sd-note">
      <p className={clamp ? "clamp" : undefined}>{n.text}</p>
      <small>
        {n.author || "ön büro"} · {trDay(n.date)}
        {!clamp && <> · {CATEGORY_TR[n.category] ?? n.category}</>}
      </small>
    </div>
  );
}

// ── What opens over the drawer ────────────────────────────
// Each in the drawer's look, "‹ name" (and ×, Escape, the backdrop)
// back to it. The drawer's read is kept across them, so it comes back
// at once; one opened before it came shows the drawer's ring.

function SubSheet({anchor, title, sub, loading, onClose, children}: {
  anchor: Anchor; title: string; sub?: string; loading: boolean; onClose: () => void;
  children: ReactNode;
}) {
  const head = (
    <header className="sd-head">
      <div className="sd-who">
        <button className="sd-chip sd-back" onClick={onClose}>
          <Icon name="back" /><span className="sd-ell">{anchorName(anchor)}</span>
        </button>
        <span className="spacer" />
        <Spin on={loading} />
        <button className="sd-round" aria-label="Kapat" onClick={onClose}><Icon name="x" /></button>
      </div>
      <h2 className="sd-title">{title}</h2>
      {sub && <p className="sd-sub">{sub}</p>}
    </header>
  );
  return <Sheet className="seans-sheet" head={head} onClose={onClose}>{children}</Sheet>;
}

// "WhatsApp onayı": what was asked and what came back, and the send —
// with today's rules: the pilot, an answer already in, an iptal, and
// the start of the seans. A cancelled slot's rows are read only.
export function WaSheet({anchor, detail, loading, busy, today, nowMin, onSend, onClose}: {
  anchor: Anchor; detail: SessionDetail | null; loading: boolean; busy: string | null;
  today: string; nowMin: number;
  onSend: (cell: BoardCell, client: BoardClient) => void; onClose: () => void;
}) {
  const {cell, rec} = anchor;
  if (!cell) {
    const kids = detail?.clients ?? [];
    return (
      <SubSheet anchor={anchor} title="WhatsApp onayı"
        sub={rec.time ? `${rec.time}–${rec.endTime ?? "?"} seansı` : "saatsiz seans"}
        loading={loading} onClose={onClose}>
        {!detail && !loading && <p className="sd-quiet">Ayrıntı yok.</p>}
        {kids.map((c) => (
          <section key={c.clientId} className="sd-card sd-pad">
            {kids.length > 1 && <h3 className="sd-card-title">{c.name}</h3>}
            <WhatsappRow wa={c.whatsapp} cellTime={rec.time} />
            <p className="sd-quiet">
              {rec.kind === "paused" ? "Ara verilmiş seans." : "İptal edilmiş seans."}
            </p>
          </section>
        ))}
      </SubSheet>
    );
  }
  const cancelled = cell.status === "cancelled" || cell.status === "paused";
  const started = hasStarted(cell.start.slice(0, 10), cell.time, today, nowMin);
  const group = cell.clients.length > 1;
  return (
    <SubSheet anchor={anchor} title="WhatsApp onayı" sub={`${cell.time}–${cell.endTime} seansı`}
      loading={loading} onClose={onClose}>
      {cell.clients.map((c) => {
        const wa = detail?.clients.find((x) => x.clientId === c.clientId)?.whatsapp ?? null;
        const {state, source, eligible} = c.attendance;
        const why = cancelled ? "İptal edilmiş seans." :
          started ? "Seans başladı — onay artık sorulmaz." :
          !eligible ? "WhatsApp onayları bu danışan için kapalı (pilot dışı ya da kapalı)." :
          state === "confirmed" && source === "phone" ? "Telefonda onaylandı." :
          state === "confirmed" || state === "declined" ?
            `Yanıt geldi: ${waWord(state)?.word ?? state}.` : null;
        const sending = busy === `wa:${cell.id}:${c.clientId}`;
        return (
          <section key={c.clientId} className="sd-card sd-pad">
            {group && <h3 className="sd-card-title">{c.name}</h3>}
            <WhatsappRow wa={wa} cellTime={cell.time} />
            <button className="sd-go" disabled={why !== null || busy !== null}
              onClick={() => onSend(cell, c)}>
              {sending ? "Gönderiliyor…" : "Onay mesajı gönder"}
            </button>
            {why && <p className="sd-quiet">{why}</p>}
          </section>
        );
      })}
    </SubSheet>
  );
}

// "Seans bilgisi": the seans as the calendar, the program and the
// iptal record know it.
export function SeansInfoSheet({anchor, providerName, detail, loading, onClose}: {
  anchor: Anchor; providerName: string; detail: SessionDetail | null; loading: boolean;
  onClose: () => void;
}) {
  const {cell, rec} = anchor;
  const span = cell ? `${cell.time}–${cell.endTime}` :
    rec.time ? `${rec.time}–${rec.endTime ?? "?"}` : "saatsiz";
  const sign = cell ? STATUS_TR[cell.status] :
    rec.kind === "paused" ? STATUS_TR.paused : STATUS_TR.cancelled;
  return (
    <SubSheet anchor={anchor} title="Seans bilgisi"
      sub={`${span} · ${providerName} · ${sign.word}`} loading={loading} onClose={onClose}>
      {(detail || !loading) && (
        <section className="sd-card sd-pad">
          {detail ? <SessionKv detail={detail} cellTime={cell ? cell.time : rec.time} /> :
            <p className="sd-quiet">Ayrıntı yok.</p>}
        </section>
      )}
    </SubSheet>
  );
}

// "Seanslar ve ödemeler": every seans the door sent, month by month,
// and the money section — what the desk takes today, the open
// seanslar, the last payments, the warnings, the door's sentence.
export function HistorySheet({anchor, client, detail, loading, money, today, onClose}: {
  anchor: Anchor; client: ClientRef; detail: SessionDetail | null; loading: boolean;
  money: boolean; today: string; onClose: () => void;
}) {
  const {cell, rec} = anchor;
  const providerId = cell ? cell.providerId : rec.providerId;
  const cd = detail?.clients.find((c) => c.clientId === client.clientId);
  const board = cell?.clients.find((c) => c.clientId === client.clientId);
  const open = !!cell && cell.status !== "cancelled" && cell.status !== "paused" &&
    !board?.recorded;
  const months: [string, SessionHistoryRow[]][] = [];
  for (const r of cd?.history ?? []) {
    const last = months[months.length - 1];
    if (last && last[0] === r.date.slice(0, 7)) last[1].push(r);
    else months.push([r.date.slice(0, 7), [r]]);
  }
  return (
    <SubSheet anchor={anchor} title="Seanslar ve ödemeler" sub={client.name} loading={loading}
      onClose={onClose}>
      {!cd && !loading && <p className="sd-quiet">Ayrıntı yok.</p>}
      {money && cd?.history && (
        <section className="sd-card sd-list">
          {months.length === 0 && <p className="sd-quiet">Kayıtlı seans yok.</p>}
          {months.map(([month, rows]) => (
            <div key={month} className="sd-month">
              <h3>{trMonth(month)}</h3>
              <HistoryRows rows={rows} providerId={providerId} today={today} />
            </div>
          ))}
        </section>
      )}
      {money && cd?.money && (
        <section className="sd-card sd-pad">
          <Money m={cd.money} providerId={providerId} sessionOpen={open} />
        </section>
      )}
    </SubSheet>
  );
}

// "Notlar": every note the door sent, newest first.
export function NotesSheet({anchor, client, detail, loading, busy, onNote, onClose}: {
  anchor: Anchor; client: ClientRef; detail: SessionDetail | null; loading: boolean;
  busy: string | null; onNote: (client: ClientRef) => void; onClose: () => void;
}) {
  const notes = detail?.clients.find((c) => c.clientId === client.clientId)?.notes;
  return (
    <SubSheet anchor={anchor} title="Notlar" sub={client.name} loading={loading} onClose={onClose}>
      <section className="sd-card sd-list">
        <div className="sd-list-head">
          <h3>Son notlar</h3>
          <button className="sd-chip small" disabled={busy !== null} onClick={() => onNote(client)}>
            <Icon name="plus" />Not düş
          </button>
        </div>
        {notes === undefined && !loading && <p className="sd-quiet">Notlar okunamadı.</p>}
        {notes === null && <p className="sd-quiet">Not okuma yetkiniz yok.</p>}
        {notes?.length === 0 && <p className="sd-quiet">Not yok.</p>}
        {notes && notes.length > 0 && (
          <ul className="sd-notes">
            {notes.map((n) => <li key={n.noteId}><NoteItem n={n} /></li>)}
          </ul>
        )}
      </section>
    </SubSheet>
  );
}

// ── The full sections, as the old drawer had them ─────────

function Sec({children}: {children: ReactNode}) {
  return <h4 className="sec">{children}</h4>;
}

function WhatsappRow({wa, cellTime}: {wa: WaRowView | null; cellTime: string | null}) {
  if (!wa) return <p className="sd-quiet">Bu seans için sorulmadı.</p>;
  return (
    <div className="lines">
      {wa.response?.source === "phone" ? (
        <div className="wa-reply">
          Telefonda onaylandı
          {wa.response.at && <span className="muted"> · {trStamp(wa.response.at)}</span>}
          {wa.response.by && <span className="muted"> · {wa.response.by}</span>}
        </div>
      ) : null}
      {wa.delivery !== "phone" && <div>
        {trStamp(wa.createdAt)} soruldu
        {wa.contactName && <> ({wa.contactName})</>}
        {cellTime && wa.sessionTime && wa.sessionTime !== cellTime &&
          <> — <b>{wa.sessionTime}</b> için</>}
        {" · "}{DELIVERY_TR[wa.delivery] ?? wa.delivery}
        {wa.error && <> · <span className="error">{wa.error}</span></>}
      </div>}
      {wa.response && wa.response.source !== "phone" && (
        <div className="wa-reply">
          {WA_REPLY_TR[wa.response.kind] ?? wa.response.kind}
          {wa.response.at && <span className="muted"> · {trStamp(wa.response.at)}</span>}
          {wa.response.kind === "text" && <>: “{wa.response.text}”</>}
        </div>
      )}
      {wa.autoCancel && (
        <div className="muted small">{AUTO_CANCEL_TR[wa.autoCancel] ?? wa.autoCancel}</div>
      )}
    </div>
  );
}

// The money section leads with the one figure the desk acts on — what
// to take from this family today — and under it the pieces that make
// it: this seans' fee, what earlier seanslar left open, the credit.
function Money({m, providerId, sessionOpen}: {
  m: ClientMoneyView; providerId: string; sessionOpen: boolean;
}) {
  const lockedHere = m.restrictedCredit
    .find((r) => r.providerId === providerId)?.amountTL ?? 0;
  const due = dueToday(m, lockedHere, sessionOpen);
  // Gizli mod: the arithmetic line is figures through and through, so
  // it goes; the door's sentences lose their amounts.
  const {blind, tl, fee, text} = useMoney();
  return (
    <>
      <Sec>Para</Sec>
      <p className="due-line">
        <span>Bugün alınacak</span>
        <b className={due.amountTL !== null && due.amountTL > 0 ? "amount-neg" : ""}>
          {due.amountTL === null ? "ücret tanımsız" : tl(due.amountTL)}
        </b>
        {!blind && due.how && <span className="muted small">= {due.how}</span>}
        {due.amountTL === 0 && m.creditTL + lockedHere > 0 && (
          <span className="muted small">— kredi karşılıyor, tahsilat gerekmez</span>
        )}
      </p>
      <div className="money-grid">
        <span>Bu seansın ücreti <b>{fee(m.feeTL)}</b></span>
        <span>
          {sessionOpen ? "Önceki seanslardan kalan" : "Açık borç"}{" "}
          <b className={m.collectNowTL > 0 ? "amount-neg" : ""}>{tl(m.collectNowTL)}</b>
        </span>
        <span>Kredi <b>{tl(m.creditTL)}</b></span>
        {m.restrictedCredit.map((r) => (
          <span key={r.providerId}>
            {r.providerName}'e kilitli kredi <b>{tl(r.amountTL)}</b>
          </span>
        ))}
      </div>
      <p className="muted small">{text(m.explanation)}</p>
      {m.openServices.length > 0 && (
        <>
          <span className="muted small">Açık seanslar</span>
          <ul className="lines">
            {m.openServices.map((s) => (
              <li key={s.serviceId}>
                <span className="time">{trDay(s.date)}</span>
                <span>{s.providerName}</span>
                <span className="muted">{SERVICE_KIND_TR[s.kind] ?? s.kind}</span>
                <span>{tl(s.feeTL)}</span>
                {s.remainingTL !== s.feeTL && <span className="amount-neg">kalan {tl(s.remainingTL)}</span>}
                {s.carriedFromOldSystem && <span className="muted small">(eski sistemden)</span>}
              </li>
            ))}
          </ul>
        </>
      )}
      <span className="muted small">Son ödemeler</span>
      {m.payments.length === 0 ? <p className="muted small">Ödeme kaydı yok.</p> : (
        <ul className="lines">
          {m.payments.map((x) => (
            <li key={x.paymentId}>
              <span className="time">{trDay(x.date)}</span>
              <b className={x.amountTL < 0 ? "amount-neg" : ""}>{tl(x.amountTL)}</b>
              <span>{CHANNEL_TR[x.channel] ?? x.channel}</span>
              {x.purpose === "refund" && <span className="muted">iade</span>}
              {x.serviceDates.length > 0 && (
                <span className="muted small">
                  {x.serviceDates.map(trDay).join(", ")} seansı için
                </span>
              )}
              {x.note && <span className="muted small">— {x.note}</span>}
            </li>
          ))}
        </ul>
      )}
      {m.warnings.map((w) => <p key={w} className="muted small">⚠ {text(w)}</p>)}
    </>
  );
}

function SessionKv({detail, cellTime}: {detail: SessionDetail; cellTime: string | null}) {
  return (
    <div className="kv">
      <span className="k">Takvim</span>
      <span><EventLine e={detail.event} cellTime={cellTime} /></span>
      <span className="k">Program</span>
      <span><ScheduleLine s={detail.schedule} /></span>
      <span className="k">İptal kaydı</span>
      <span><CancellationLine c={detail.cancellation} /></span>
    </div>
  );
}

function EventLine({e, cellTime}: {e: SessionEventView | null; cellTime: string | null}) {
  if (!e) return <span className="muted">takvimde bulunamadı</span>;
  const tag = /\[Nefes session: [^\]]+\]/;
  const note = (e.description ?? "").replace(tag, "").trim();
  return (
    <>
      “{e.summary || "—"}”
      {e.time && <> · {e.time}–{e.endTime}{e.durationMin !== null && <> ({e.durationMin} dk)</>}</>}
      {e.time && cellTime && e.time !== cellTime && <b> — takvimde saati değişmiş</b>}
      {" · "}{e.recurringEventId ? "serinin bir örneği" : "tek etkinlik"}
      {!e.live && <> · <span className="error">takvimde değil ({e.status})</span></>}
      {e.updated && <span className="muted small"> · güncellendi {trStamp(e.updated)}</span>}
      {note && <div className="muted small note-text">{note}</div>}
    </>
  );
}

function ScheduleLine({s}: {s: ScheduleView | null}) {
  if (!s) return <span className="muted">yok — elle oluşturulmuş etkinlik</span>;
  return (
    <>
      {RECURRENCE_TR[s.recurrence] ?? s.recurrence} {WEEKDAY_TR[s.dayOfWeek] ?? ""} {s.time}
      {" · "}{s.durationMin} dk · {FORMAT_TR[s.format] ?? s.format}
      {s.room && <> · {ROOM_TR[s.room] ?? s.room}</>}
      {s.clientNames.length > 0 && <> · {s.clientNames.join(", ")}</>}
      {!s.linked && <b> — bu etkinlik programa bağlı değil</b>}
      {s.notes && <div className="muted small note-text">{s.notes}</div>}
    </>
  );
}

function CancellationLine({c}: {c: CancellationView | null}) {
  if (!c) return <span className="muted">yok</span>;
  const sign = c.kind === "paused" ? STATUS_TR.paused : STATUS_TR.cancelled;
  return (
    <>
      {sign.icon} {sign.word} · {trStamp(c.createdAt)} {CANCELLED_BY_TR[c.cancelledBy] ?? c.cancelledBy}: {c.reason}
      <span className="muted small"> — {c.recordedBy} ({SOURCE_TR[c.source] ?? c.source})</span>
      {c.restoredAt && (
        <div className="muted small">geri alındı {trStamp(c.restoredAt)}{c.restoredBy && <> — {c.restoredBy}</>}</div>
      )}
      {c.standing && !c.restorable && (
        <div className="muted small">kayıtta saat bilgisi yok — geri almak için sohbette yeniden oluşturun</div>
      )}
    </>
  );
}
